import { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Globe2, Users, UsersRound, MapPin, Megaphone, ListChecks,
  Contact2, DollarSign, TrendingUp, Wallet, FileBarChart, Bot, Bell, Settings as Cog,
  Search, LogOut, Menu, X, ScrollText,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/global-operations", label: "Global Operations", icon: Globe2 },
  { to: "/admin/workers", label: "Workers", icon: Users },
  { to: "/admin/teams", label: "Teams", icon: UsersRound },
  { to: "/admin/locations", label: "Locations", icon: MapPin },
  { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/admin/tasks", label: "Tasks", icon: ListChecks },
  { to: "/admin/leads", label: "Leads", icon: Contact2 },
  { to: "/admin/sales", label: "Sales", icon: DollarSign },
  { to: "/admin/performance", label: "Performance", icon: TrendingUp },
  { to: "/admin/payments", label: "Payments", icon: Wallet },
  { to: "/admin/reports", label: "Reports", icon: FileBarChart },
  { to: "/admin/ai-operations", label: "AI Operations", icon: Bot },
  { to: "/admin/notifications", label: "Notifications", icon: Bell },
  { to: "/admin/audit-logs", label: "Audit Log", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: Cog },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => { setOpen(false); }, [location.pathname]);
  const loadUnread = useCallback(() => {
    api.get("/notifications").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, []);
  useEffect(() => { loadUnread(); const i = setInterval(loadUnread, 30000); return () => clearInterval(i); }, [loadUnread]);

  const search = async (v) => {
    setQ(v);
    if (v.length < 2) { setResults(null); return; }
    try { const { data } = await api.get(`/search?q=${encodeURIComponent(v)}`); setResults(data); } catch {}
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] text-slate-200 flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-50 inset-y-0 left-0 w-64 bg-[#0B0F19] border-r border-white/10 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-white/10">
          <Logo dark />
          <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto cc-scroll py-3 px-3 space-y-0.5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`sidebar-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-[#1D4ED8] text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>
      {open && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 flex items-center gap-3 px-4 sm:px-6 border-b border-white/10 bg-[#0B0F19] sticky top-0 z-30">
          <button className="lg:hidden text-slate-300" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu size={22} /></button>
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input data-testid="admin-global-search-input" value={q} onChange={(e) => search(e.target.value)} placeholder="Search workers, campaigns, leads…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#151D2A] border border-white/10 text-sm text-white focus:border-[#1D4ED8] outline-none" />
            {results && (
              <div className="absolute mt-2 w-full bg-[#151D2A] border border-white/10 rounded-xl shadow-2xl p-2 max-h-80 overflow-y-auto cc-scroll z-50">
                {["workers", "campaigns", "leads"].map((k) => (results[k]?.length > 0) && (
                  <div key={k} className="mb-2">
                    <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-slate-500">{k}</div>
                    {results[k].map((item) => (
                      <button key={item.id} onClick={() => { setResults(null); setQ(""); navigate(k === "workers" ? "/admin/workers" : k === "campaigns" ? "/admin/campaigns" : "/admin/leads"); }}
                        className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-white/5 text-sm text-slate-200">
                        {item.name || item.company} <span className="text-slate-500 text-xs">{item.worker_id || item.campaign_id || item.lead_id}</span>
                      </button>
                    ))}
                  </div>
                ))}
                {(!results.workers?.length && !results.campaigns?.length && !results.leads?.length) && <div className="px-3 py-3 text-sm text-slate-500">No matches</div>}
              </div>
            )}
          </div>
          <button data-testid="admin-notifications-btn" onClick={() => navigate("/admin/notifications")} className="relative p-2 rounded-lg hover:bg-white/5 text-slate-300">
            <Bell size={20} />
            {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-[#1D4ED8] text-[10px] font-bold text-white grid place-items-center">{unread}</span>}
          </button>
          <div className="flex items-center gap-3 pl-2 border-l border-white/10">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-white leading-none">{user?.name}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{user?.role?.replace(/_/g, " ")}</div>
            </div>
            <div className="h-9 w-9 rounded-full bg-[#1D4ED8] grid place-items-center font-bold text-white text-sm">{(user?.name || "?")[0]}</div>
            <button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/login"); }} className="p-2 rounded-lg hover:bg-white/5 text-slate-400" title="Logout"><LogOut size={18} /></button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 overflow-x-hidden"><Outlet /></main>
      </div>
    </div>
  );
}
